import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';

// Scale the preview only. Canvas pixels and exported image dimensions stay untouched.
const CanvasViewport = ({
  width,
  height,
  children,
  canMoveImages,
  isMoveMode,
  onMoveModeChange,
}) => {
  const viewport = useRef(null);
  const [size, setSize] = useState({
    width: 0,
    height: window.innerHeight,
    compact: window.innerWidth < 900,
  });

  useLayoutEffect(() => {
    const measure = () =>
      setSize({
        width: viewport.current.clientWidth,
        height: window.innerHeight,
        compact: window.innerWidth < 900,
      });
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(viewport.current);
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Include the canvas border so neither edge gets clipped at narrow widths.
  const stageWidth = width + 2;
  const stageHeight = height + 2;
  const maxHeight = Math.max(160, size.height * (size.compact ? 0.5 : 0.62));
  const scale = Math.min(1, (size.width || stageWidth) / stageWidth, maxHeight / stageHeight);

  return (
    <div className="canvas-viewport" ref={viewport}>
      <div
        className="canvas-fit"
        style={{ width: stageWidth * scale, height: stageHeight * scale }}
      >
        <div
          className="canvas-native"
          style={{ width: stageWidth, height: stageHeight, transform: `scale(${scale})` }}
        >
          {children(scale, size.compact)}
        </div>
      </div>
      {size.compact && canMoveImages && (
        <div className="canvas-touch-controls">
          <Button
            size="small"
            color="primary"
            variant={isMoveMode ? 'contained' : 'outlined'}
            aria-pressed={isMoveMode}
            onClick={onMoveModeChange}
          >
            {isMoveMode ? 'Done moving' : 'Move images'}
          </Button>
          <span>{isMoveMode ? 'Drag an image to position it' : 'Swipe to scroll the page'}</span>
        </div>
      )}
    </div>
  );
};

CanvasViewport.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  children: PropTypes.func.isRequired,
  canMoveImages: PropTypes.bool,
  isMoveMode: PropTypes.bool,
  onMoveModeChange: PropTypes.func,
};

export default CanvasViewport;
